/**
 * What the four measured documents send, what they ask before sending it, and what
 * they deliberately do not (analytics ticket 02).
 *
 * `/`, `/how-it-works`, `/privacy` and `/404` count their visits with Google
 * Analytics. **`/app/` does not, and nothing here ships to it** — this is an
 * entry point of its own rather than a line in `main.tsx`, so the prompter's
 * bundle never contains a word of it. That separation is the ticket: the claim
 * this product makes is about the audio, the video and the recognised text, all
 * three of which live in the application, and none of which this file can see.
 *
 * **A classic script rather than a module**, built `iife` in `site.ts`, for the
 * same reason `link-through.ts` is one and a sharper version of it. A module is
 * deferred, so it would run *after* `gtag.js` had loaded and started — and the
 * consent defaults below are only defaults if they are set first. Deferred, they
 * would arrive as a change of mind, after the cookie. That argument got stronger
 * when the cookie became real: it used to cost a state nobody was in, and it now
 * costs a cookie set on somebody who was never asked.
 *
 * **It makes one request, and `network-audit.ts` carries a policy saying so.**
 * That is new — this file used to contain no network primitive at all — and the
 * request is same-origin, to this site's own Worker, and carries nothing about
 * the visitor except the question it is asking. The audit's policy is the place
 * that has to stay true; see `CONSENT_REGION`.
 *
 * What bounds `gtag.js` is not here and never was: Google's code is not compiled
 * by this repository and no amount of reading source text bounds it. What bounds
 * it is the content security policy those four pages are served under —
 * `MARKETING_DIRECTIVES` in `scripts/csp.ts` — which names the one host it may be
 * fetched from and the three it may report to, and nothing else. Cross-origin
 * isolation is off on those four pages and on no others.
 */

/**
 * The property, and it is written here rather than imported.
 *
 * `ships-to-the-browser.test.ts` forbids anything under `page/` importing the
 * build's own directory, so `scripts/head.ts` — which writes the `gtag.js` tag
 * naming this same id — cannot hand it over. The two are held equal by a row in
 * `head.test.ts`, which is the rope `THE_APP` in `link-through.ts` and
 * `CANONICAL_ORIGIN` are both already on. Two ids that drifted would be a page
 * reporting to a property nobody reads.
 */
const MEASUREMENT_ID = "G-6FG0JGNG9C";

/** Where the application is, and therefore what a click worth counting goes to.
 * The trailing slash is `APP_PATH`'s, and for its reasons. */
const THE_APP = "/app/";

/**
 * Where to ask whether this visitor has to be asked.
 *
 * Restated rather than imported, and for the same rule as `MEASUREMENT_ID`:
 * `scripts/consent-region.ts` is the Worker's half and `page/` may not reach into
 * `scripts/`. `head.test.ts` holds the two equal, because a path that drifted
 * would 404, the catch below would read that as "no idea", and every visitor on
 * earth would get a banner they did not need — which is the failure that looks
 * like a design decision rather than a bug.
 */
const CONSENT_REGION = "/consent-region";

/** Where the answer is kept, so the question is asked once rather than every
 * page. Namespaced because this origin's local storage is also the prompter's. */
const CHOICE_KEY = "cuekards:analytics-consent";

const GRANTED = "granted";
const DENIED = "denied";

/**
 * The banner's words, and they are here rather than in `site-copy.ts`.
 *
 * That is against this repository's usual rule and is a deliberate exception with
 * a mechanical reason: `site-copy.ts` imports `capabilities.ts` and
 * `landing-copy.ts`, both of which import `@cuekards/core`. Reaching for four
 * sentences would put the application's own package inside the one script that
 * runs, classic and undeferred, in the head of every marketing page. The copy rule
 * exists to keep words out of renderers; this file is not a renderer, and nothing
 * it says is ever written into the built HTML — the banner is constructed at run
 * time and only for the visitors who have to see it.
 */
const BANNER_QUESTION =
  "Can we count this visit? We use Google Analytics to see which pages get " +
  "read. It sets one cookie, it never sees anything you write or record, and " +
  "saying no changes nothing else about the site.";
const BANNER_ACCEPT = "Allow";
const BANNER_DECLINE = "No thanks";
/** The dialogue's accessible name. A `role="dialog"` with no name is announced as
 * "dialog" and nothing else, which is the worst of both. */
const BANNER_LABEL = "Analytics";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

window.dataLayer = window.dataLayer || [];

/**
 * Google's own shim, written out rather than pasted in.
 *
 * **It pushes `arguments` and not a copy**, and that is upstream's specification
 * rather than a flourish: `gtag.js` reads what it finds in `dataLayer` and some
 * of its paths test for a genuine `Arguments` object, so an array built from a
 * rest parameter is the version of this that works until one day it does not.
 * The rest parameter is declared anyway, and only so that the call sites below
 * typecheck — it is deliberately not the thing that gets pushed.
 */
function gtag(..._args: unknown[]): void {
  window.dataLayer!.push(arguments);
}

/**
 * **Denied, before anything is loaded.**
 *
 * All four keys are denied here and at least three of them stay that way for the
 * life of the visit. This is the state `gtag.js` must find when it starts, and the
 * only reason the script order in `analyticsTags` is load-bearing.
 *
 * The three advertising keys are denied because nothing here advertises, and a
 * default left unstated is a default Google gets to choose later.
 * `analytics_storage` is denied *provisionally*: it is the answer for a visitor
 * whose region is unknown, whose answer was no, or who never got as far as being
 * asked, and it is the answer that holds if anything below this line fails.
 * Failing closed is the whole design — every error path in this file lands on a
 * banner or on silence, and none of them lands on a cookie.
 */
gtag("consent", "default", {
  ad_storage: DENIED,
  ad_user_data: DENIED,
  ad_personalization: DENIED,
  analytics_storage: DENIED,
});

/**
 * The one event worth sending: somebody went from the site to the application.
 *
 * It is the question this whole effort was raised to answer — a front page is
 * either turning readers into users or it is not — and it is the one thing GA
 * cannot see for itself, because `/app/` is not measured and so the arrival at
 * the other end is never recorded.
 *
 * **Bound before the region is known, deliberately.** Everything else here waits
 * on a round trip, and a reader who clicks the call to action inside that window
 * would otherwise be the one click that mattered most and counted least. A push
 * onto `dataLayer` before `config` is queued, not lost; if consent never arrives
 * the queue is never drained, which is the correct outcome and not a leak.
 *
 * **Delegated on the document rather than bound to the buttons**, because the
 * call to action is rendered in three places on `/` (the hero, the bar and the
 * closing band) and linked from `/how-it-works` besides, and a listener per
 * element is four chances to add a fifth and not notice. `closest` also means a
 * click on whatever is *inside* the link still counts, which on a styled button
 * is most of them.
 *
 * No `preventDefault`, no waiting for the beacon, no `event_callback` delaying
 * the navigation. The measurement is worth exactly nothing next to the click, and
 * a call to action that hesitates to phone Google first is a worse front page
 * than one with no analytics at all.
 */
document.addEventListener("click", (event) => {
  const link = (event.target as Element | null)?.closest?.("a");
  if (link === null || link === undefined) return;
  // `pathname` rather than the raw attribute: the links are written root-relative
  // in some places and resolved in others, and what is being asked is where this
  // goes, not how it was spelled.
  if (new URL(link.href, location.href).pathname !== THE_APP) return;
  gtag("event", "start_app", { link_text: link.textContent?.trim().slice(0, 100) });
});

/**
 * Start measuring, once.
 *
 * **`config` is withheld until the consent state is settled**, which is the one
 * sequencing decision in this file that is not Google's. Fired on load it would
 * spend the page view immediately, as a denied cookieless ping — a hit that is
 * kept out of every report and cannot be redeemed by a later `consent update`. So
 * the visitor who accepts would have sat through the banner and still not been
 * counted for the page they were reading when they saw it.
 *
 * The cost is that a measured page view happens one same-origin round trip after
 * load rather than at it, and for an EEA visitor not until they answer. That is
 * the right way round: a page view attributed to a consent state the visitor was
 * never in is worth less than no page view at all.
 */
let started = false;
function start(): void {
  if (started) return;
  started = true;
  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID);
}

/** Granted, and then counted. Never one without the other. */
function grantAndStart(): void {
  gtag("consent", "update", { analytics_storage: GRANTED });
  start();
}

/**
 * The answer from last time, or nothing.
 *
 * Wrapped because reading local storage *throws* rather than returning null in a
 * tab with site data blocked, which is a setting somebody reading this particular
 * privacy page is more likely than average to have turned on. The failure is
 * "ask again", which is correct and merely repetitive.
 */
function remembered(): string | null {
  try {
    return localStorage.getItem(CHOICE_KEY);
  } catch {
    return null;
  }
}

function remember(choice: string): void {
  try {
    localStorage.setItem(CHOICE_KEY, choice);
  } catch {
    // Then it is asked again next time. Storing the answer is a convenience;
    // honouring it within this page is not, and that has already happened.
  }
}

/**
 * The question, drawn only for the visitors who have to be asked.
 *
 * Built rather than written into the templates, and that is what keeps ADR-0022's
 * rule intact: the served HTML is one artifact for everybody, and the difference
 * between a German visitor and an American one exists only in this tab. It is also
 * why there is no marker in `landing.html` — a banner in the markup would be
 * either always present or a page that varies.
 *
 * `createElement` rather than `innerHTML`: the strings above are this repository's
 * own and contain no markup, which is exactly the condition that quietly stops
 * being true.
 */
function ask(): void {
  const banner = document.createElement("div");
  banner.className = "site-consent";
  banner.setAttribute("role", "dialog");
  banner.setAttribute("aria-label", BANNER_LABEL);

  const words = document.createElement("p");
  words.className = "site-consent-text";
  words.textContent = BANNER_QUESTION;

  const actions = document.createElement("div");
  actions.className = "site-consent-actions";

  const answer = (label: string, choice: string): HTMLButtonElement => {
    const button = document.createElement("button");
    // `type` said out loud: these are not in a form today, and a button with no
    // type inside one is a submit button.
    button.type = "button";
    button.className = `site-consent-answer site-consent-${choice}`;
    button.textContent = label;
    button.addEventListener("click", () => {
      remember(choice);
      banner.remove();
      if (choice === GRANTED) grantAndStart();
    });
    return button;
  };

  actions.append(answer(BANNER_ACCEPT, GRANTED), answer(BANNER_DECLINE, DENIED));
  banner.append(words, actions);
  document.body.append(banner);
}

/**
 * The banner needs a body to go in, and this script runs in the head.
 *
 * By the time anything below has awaited a round trip the document is parsed in
 * every case anyone has measured — but "in every case measured" is not a
 * guarantee, and the failure it is not would be a question silently never asked.
 */
function whenThereIsABody(draw: () => void): void {
  if (document.body !== null) {
    draw();
    return;
  }
  document.addEventListener("DOMContentLoaded", draw, { once: true });
}

/**
 * Where the visit stands: granted outright, refused, or a question.
 *
 * **Every failure here means "ask".** A network error, a 404 from a path that has
 * drifted, a body that is not the JSON this expects, a field that is not `false` —
 * each of them is a case where this code does not know where the visitor is, and
 * the honest reading of that is the one that sets no cookie. The shape of the test
 * is `!== false` rather than `=== true` for that reason.
 */
async function decide(): Promise<void> {
  let mustAsk = true;
  try {
    const answer = await fetch(CONSENT_REGION, {
      // No credentials on the one request that exists to avoid a cookie.
      credentials: "omit",
    });
    if (answer.ok) {
      mustAsk = ((await answer.json()) as { ask?: unknown }).ask !== false;
    }
  } catch {
    // Fail closed. See above.
  }

  if (!mustAsk) {
    grantAndStart();
    return;
  }

  const choice = remembered();
  if (choice === GRANTED) {
    grantAndStart();
    return;
  }
  // A remembered no is honoured in silence: no banner, and no `config` either, so
  // not one request is made to Google for a visitor who said not to.
  if (choice === DENIED) return;

  whenThereIsABody(ask);
}

void decide();

export {};
